import { StethoscopeIcon } from "assets/icons/Stethoscope";
import { AccountFeeType } from "constants/fee";
import { validationConstants } from "constants/formValidation";
import { calculateTax, centsToDollars, parsePrice } from "helpers/price";
import useFormValidation from "hooks/useFormValidation";
import { useGetAccountDiscountAndFee } from "hooks/useGetAccountDiscountAndFee";
import {
  useProviderProcedure,
  CLEAR_HEALTH_PAYER_NAME,
  // CPT_SEARCH_REQUIRES_VISIT_DATE_MESSAGE,
} from "hooks/useProviderProcedure";
import { sizer } from "layout/styles/styled/sizer";
import { StyledFieldGrid } from "pages/CreateOrder/CreateOrder.styled";
import {
  formatDateOfServiceForProviderProcedureSearch,
  // isDateOfServiceValidForCptSearch,
} from "pages/CreateOrder/utils/helpers";
import { AutoCompleteSelectParams } from "primereact/autocomplete";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputNumber } from "primereact/inputnumber";
import { useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { IServiceDetailsState } from "store/slices/serviceDetails";

import { ProcedureRow } from "../../../../../../types/CreateOrder";
import {
  SpecialtyList,
  SpecialtyListDict,
} from "../../../../../../types/Provider/Provider";
import { StepCard } from "../../../StepCard";
import { StepKey } from "../../../StepCard/helper";
import { useStepStatus } from "../../../StepCard/useStepStatus";
import {
  buildClearPackageTotalAmount,
  buildDescriptionColumnTemplate,
  buildSelectedProceduresObject,
} from "../../builders";
import { autoCompleteHandleOnUnSelectProcedureNew } from "../../handlers";
import { AutoCompleteCPT } from "../Common/AutoCompleteCPT";
import { BenefitCell } from "../Common/BenefitCell";
import { CptCodePill } from "../Common/CptCodePill";
import { buildCptGroups, CptGroup } from "../Common/cptGroups";
import { NoServices } from "../Common/NoServices";
import { ProviderDropdown } from "../Common/ProviderDropdown";
import { QuantityCell } from "../Common/QuantityCell";
import { RemoveItem } from "../Common/RemoveItem";
import { ServicesFooter } from "../Common/ServicesFooter";
import {
  StyledBundlePanel,
  StyledBundleRow,
  StyledServicesTable,
  StyledTrashButton,
} from "../Common/styled";

export function DefaultServiceDetails({
  initialProviderProcedures,
  redo,
  draft,
}: {
  initialProviderProcedures?: any;
  redo?: boolean;
  draft?: boolean;
}) {
  const {
    control,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext();
  const dateOfServiceWatch = watch("dateOfService");
  const notScheduledYetField = watch("notScheduledYet");
  /* Strict Visit date before CPT search (re-enable with hook + AutoComplete disabled/helper below):
  const orderType = watch("orderType");
  const dateOfServiceValidForCptSearch = useMemo(
    () => isDateOfServiceValidForCptSearch(dateOfServiceWatch, orderType),
    [dateOfServiceWatch, orderType]
  );
  const dateOfServiceForSearch = useMemo(
    () =>
      dateOfServiceValidForCptSearch
        ? formatDateOfServiceForProviderProcedureSearch(dateOfServiceWatch)
        : undefined,
    [dateOfServiceWatch, dateOfServiceValidForCptSearch]
  );
  const canSearchCptByVisitDate = dateOfServiceValidForCptSearch;
  */
  const dateOfServiceForSearch = useMemo(
    () =>
      notScheduledYetField
        ? undefined
        : formatDateOfServiceForProviderProcedureSearch(dateOfServiceWatch),
    [dateOfServiceWatch]
  );
  const initialClearFeeValueInPercentage = watch(
    "initialClearFeeValueInPercentage"
  );
  const { handleOnBlurField, getFormErrorMessage } = useFormValidation();
  const dispatch = useDispatch();
  const status = useStepStatus(StepKey.SERVICES);
  const [currentProviderProcedures, setCurrentProviderProcedures] =
    useState<any>(initialProviderProcedures || []);

  const {
    accountDiscount: accountFeeFromAccount,
    isLoading: isDiscountLoading,
  } = useGetAccountDiscountAndFee(AccountFeeType.ClearTransactionFee);

  // For redo/draft: ALWAYS use the order's saved value from the order table.
  // If the order has no clearFee (null or 0): use 0, do NOT fall back to account
  // For initial creation/copy: use the account table fee
  const accountFee =
    redo || draft
      ? initialClearFeeValueInPercentage ?? 0
      : accountFeeFromAccount;

  const { procedures, providerProcedures, search } = useProviderProcedure({
    payerNameField: CLEAR_HEALTH_PAYER_NAME,
    dateOfService: dateOfServiceForSearch,
  });

  const {
    rows,
    autoCompleteValue,
    dataTableRows,
    selectedProcedures,
  }: IServiceDetailsState = useSelector(
    (state: RootState) => state.serviceDetailsSlice
  );

  useEffect(() => {
    setValue("amount", centsToDollars(buildClearPackageTotalAmount(rows)));

    setValue(
      "totalAmount",
      centsToDollars(
        calculateTax(buildClearPackageTotalAmount(rows), accountFee)
      )
    );

    setValue(
      "serviceDetails",
      rows.map((row: ProcedureRow) => ({
        code: row.cptCode,
        specialty: row.specialty,
        amountInCents: row.amount,
        procedureId: row.procedureId,
        cptDescription: row.description,
        providerId: row.selectedProvider.id,
        providerName: row.selectedProvider.name,
        providerProcedureId: row.providerProcedureId,
        benefitsForProvProc: row.benefitSelectionType
          ? {
              benefitSelectionType: row.benefitSelectionType,
            }
          : undefined,
      }))
    );
  }, [rows, accountFee]);

  useEffect(() => {
    setValue("isDiscountLoading", isDiscountLoading);
  }, [isDiscountLoading]);

  useEffect(
    () => () => {
      setValue("isDiscountLoading", false);
    },
    []
  );

  useEffect(() => {
    if (initialProviderProcedures?.length) {
      setCurrentProviderProcedures(initialProviderProcedures);
    }
  }, [initialProviderProcedures]);

  useEffect(() => {
    if (!autoCompleteValue.length) {
      setCurrentProviderProcedures([]);
    }
  }, [autoCompleteValue]);

  // this type of orders do not have clearPatientFee and clearPatientFeePercentage, so in case user changes order type, we always reset these values to null
  useEffect(() => {
    setValue("clearPatientFee", null);
    setValue("clearPatientFeePercentage", null);
  }, []);

  const providerDropdownTemplate = (row: ProcedureRow) => {
    return (
      <ProviderDropdown
        row={row}
        currentProviderProcedures={currentProviderProcedures}
      />
    );
  };

  const actionTemplate = (row: ProcedureRow) => {
    return <RemoveItem row={row} autoCompleteValue={autoCompleteValue} />;
  };

  const cptGroups = useMemo(
    () => buildCptGroups(dataTableRows),
    [dataTableRows]
  );

  const expandedBundles = useMemo(
    () =>
      cptGroups.reduce(
        (acc, group) =>
          group.isBundle ? { ...acc, [group.cptCode]: true } : acc,
        {} as Record<string, boolean>
      ),
    [cptGroups]
  );

  const cptCodeTemplate = (group: CptGroup) => (
    <CptCodePill code={group.cptCode} />
  );

  const benefitTemplate = (group: CptGroup) => (
    <BenefitCell
      row={group.rows[0]}
      rows={rows}
      dispatch={dispatch}
      isGFE={false}
    />
  );

  const readOnlyAmount = (row: ProcedureRow) =>
    parsePrice(centsToDollars(row.amount));

  const providerCellTemplate = (group: CptGroup) =>
    group.isBundle ? (
      <span style={{ color: "var(--brand-gray-600)" }}>Multiple providers</span>
    ) : (
      providerDropdownTemplate(group.rows[0])
    );

  const amountCellTemplate = (group: CptGroup) =>
    group.isBundle ? (
      <span className="font-semibold" style={{ color: "var(--brand-black)" }}>
        {parsePrice(centsToDollars(group.totalInCents))}
      </span>
    ) : (
      readOnlyAmount(group.rows[0])
    );

  const removeCptGroup = (group: CptGroup) => {
    // The autocomplete stores "<code> - <default description>", so take the
    // description from there rather than the row, which the user may have edited.
    const defaultDescription = buildSelectedProceduresObject(
      autoCompleteValue
    ).find((procedure) => procedure.code === group.cptCode)?.cptDescription;

    autoCompleteHandleOnUnSelectProcedureNew({
      e: {
        value: `${group.cptCode} - ${defaultDescription}`,
      } as AutoCompleteSelectParams,
      autoCompleteValue,
      rows,
      dispatch,
      selectedProcedures,
    });

    setCurrentProviderProcedures(
      currentProviderProcedures?.filter(
        (pp: any) => pp?.procedure?.cptCode !== group.cptCode
      )
    );
  };

  const actionCellTemplate = (group: CptGroup) =>
    group.isBundle ? (
      <StyledTrashButton
        aria-label="Remove service"
        icon="pi pi-trash"
        className="p-button-text p-button-rounded"
        onClick={() => removeCptGroup(group)}
      />
    ) : (
      actionTemplate(group.rows[0])
    );

  const bundleTemplate = (group: CptGroup) => (
    <StyledBundlePanel className="px-3 py-3">
      <div className="flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <span
          className="flex align-items-center gap-2 text-sm font-semibold"
          style={{ color: "var(--brand-purple-700)" }}
        >
          <i className="pi pi-clone" />
          Bundled provider pricing
        </span>

        <span className="text-sm" style={{ color: "var(--brand-gray-600)" }}>
          Bundle total {parsePrice(centsToDollars(group.totalInCents))}
        </span>
      </div>

      <div className="flex align-items-center gap-3 mb-2 px-3">
        <span
          className="text-xs font-semibold uppercase"
          style={{
            width: "170px",
            color: "var(--brand-gray-600)",
            letterSpacing: "0.06em",
          }}
        >
          Amount
        </span>
        <span
          className="text-xs font-semibold uppercase"
          style={{
            width: "220px",
            color: "var(--brand-gray-600)",
            letterSpacing: "0.06em",
          }}
        >
          Specialty
        </span>
        <span
          className="flex-1 text-xs font-semibold uppercase"
          style={{ color: "var(--brand-gray-600)", letterSpacing: "0.06em" }}
        >
          Provider
        </span>
      </div>

      <div className="flex flex-column gap-2">
        {group.rows.map((providerRow) => (
          <StyledBundleRow
            key={providerRow.id}
            className="flex align-items-center gap-3 px-3 py-2"
          >
            <div style={{ width: "170px", flexShrink: 0 }}>
              {readOnlyAmount(providerRow)}
            </div>
            <div style={{ width: "220px", flexShrink: 0 }}>
              {SpecialtyListDict[providerRow.specialty as SpecialtyList]}
            </div>
            <div className="flex-1">
              {providerDropdownTemplate(providerRow)}
            </div>
            {actionTemplate(providerRow)}
          </StyledBundleRow>
        ))}
      </div>
    </StyledBundlePanel>
  );

  const quantityTemplate = (group: CptGroup) => (
    <QuantityCell
      row={group.rows[0]}
      rows={rows}
      autoCompleteValue={autoCompleteValue}
      dispatch={dispatch}
      selectedProcedures={selectedProcedures}
      procedures={procedures}
      providerProcedures={providerProcedures}
    />
  );

  return (
    <StepCard
      status={status}
      icon={<StethoscopeIcon width={20} height={20} />}
      title="Services"
      description="Add CPT codes to build the charge estimate"
      data-testid="serviceDetails_DefaultServiceDetails"
    >
      {!isDiscountLoading && (
        <StyledFieldGrid
          className="p-3 mb-4"
          style={{
            borderRadius: "12px",
            background:
              "color-mix(in srgb, var(--brand-gray-50) 40%, var(--brand-white))",
            border: "1px solid var(--brand-gray-200)",
          }}
        >
          <label htmlFor="facilityPatientEstimate">
            Facility Patient Estimate
          </label>
          <p
            className="text-sm mt-2 mb-3"
            style={{ color: "var(--brand-gray-600)" }}
          >
            Manually enter the external facility estimate.
          </p>

          <Controller
            control={control}
            name="estimatedVisitCostForPatient"
            rules={{
              required: validationConstants.SERVICE_DETAILS.FACILITY_ESTIMATE,
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <div className="flex flex-column" style={{ maxWidth: "20rem" }}>
                <InputNumber
                  value={value}
                  currency="USD"
                  locale="en-US"
                  mode="currency"
                  className="w-full"
                  inputStyle={{
                    background: "var(--brand-gray-50)",
                    borderColor:
                      "color-mix(in srgb, var(--brand-purple-700) 50%, var(--brand-white))",
                  }}
                  placeholder="Enter facility patient estimate"
                  onBlur={() =>
                    handleOnBlurField({
                      onBlur,
                      field: "estimatedVisitCostForPatient",
                      trigger,
                    })
                  }
                  inputId="facilityPatientEstimate"
                  onChange={({ value: next }) => onChange(next)}
                />

                {getFormErrorMessage("estimatedVisitCostForPatient", errors)}
              </div>
            )}
          />
        </StyledFieldGrid>
      )}

      <div className="formgrid grid w-100">
        <div className="field p-fluid col-12">
          {/*
            Visit-date CPT gate (off for now): re-import CPT_SEARCH_REQUIRES_VISIT_DATE_MESSAGE +
            isDateOfServiceValidForCptSearch, restore memos + canSearchCptByVisitDate, then add
            disabled, cptHelperText, cptHelperError on AutoCompleteCPT.
          */}
          <AutoCompleteCPT
            search={search}
            autoCompleteValue={autoCompleteValue}
            procedures={procedures}
            providerProcedures={providerProcedures}
            currentProviderProcedures={currentProviderProcedures}
            setCurrentProviderProcedures={setCurrentProviderProcedures}
          />
        </div>
      </div>

      {dataTableRows.length ? (
        <StyledServicesTable>
          <DataTable
            stripedRows
            value={cptGroups}
            dataKey="cptCode"
            expandedRows={expandedBundles}
            onRowToggle={() => {}}
            rowExpansionTemplate={(group) => bundleTemplate(group)}
            responsiveLayout="scroll"
          >
            <Column
              field="cptCode"
              header="CPT"
              headerStyle={{ width: "110px" }}
              body={(group) => cptCodeTemplate(group)}
            />
            <Column
              header="Description"
              body={(group) =>
                buildDescriptionColumnTemplate({
                  row: group.rows[0],
                  rows,
                  dispatch,
                })
              }
              style={{
                minWidth: "270px",
                maxWidth: "270px",
                paddingLeft: sizer(6),
              }}
            />

            <Column
              header="Benefit"
              headerStyle={{ width: "220px" }}
              body={(group) => benefitTemplate(group)}
            />
            <Column
              header="Amount"
              body={(group) => amountCellTemplate(group)}
              headerStyle={{ width: "250px" }}
            />
            <Column
              header="Provider"
              body={(group) => providerCellTemplate(group)}
            />
            {/* Per-procedure CPT modifier input is hidden for now (CLE-2178).
              Pricing-engine + claim-classification support stays; re-add a
              <Column> rendering <ModifiersInput row={row} /> when product wants
              order-level modifier entry. */}
            <Column
              header="Units"
              headerStyle={{ width: "110px" }}
              body={(group) => quantityTemplate(group)}
            />
            <Column
              style={{ width: "56px", textAlign: "right" }}
              body={(group) => actionCellTemplate(group)}
            />
          </DataTable>
          <ServicesFooter rows={dataTableRows} />
        </StyledServicesTable>
      ) : (
        <NoServices />
      )}
    </StepCard>
  );
}
